// plugins/sentry.client.ts
import * as Sentry from '@sentry/vue'
import { replayIntegration as sentryReplayIntegration } from '@sentry/vue'
import type { Pinia } from 'pinia'
import type { ComponentPublicInstance } from 'vue'
import { useLogger } from '~/composables/useLogger'

interface StoreState {
  user?: {
    email?: string
    settings?: unknown
    [key: string]: unknown
  }
  [key: string]: unknown
}

export default defineNuxtPlugin((nuxtApp) => {
  const config = useRuntimeConfig()
  const vueApp = nuxtApp.vueApp
  const pinia = usePinia()
  const { log: _log, logError: _logError } = useLogger()

  // Check for local testing override - only in client
  const forceSentryEnabled = import.meta.client
    ? localStorage.getItem('force_sentry_enabled') === 'true'
    : false

  // Debug logging
  _log('Sentry initialization:', {
    isProd: config.public.isProd,
    isStaging: config.public.isStaging,
    hasDSN: !!config.public.sentryDsn,
    environment: config.public.isProd ? 'production' : 'staging',
    forceSentryEnabled,
    isClient: import.meta.client,
  })

  // Initialize Sentry in production/staging or when locally forced
  if (
    (config.public.isProd || config.public.isStaging || forceSentryEnabled) &&
    import.meta.client
  ) {
    try {
      Sentry.init({
        app: vueApp,
        dsn: config.public.sentryDsn,
        environment: forceSentryEnabled ? 'local' : config.public.isProd ? 'production' : 'staging',
        debug: !config.public.isProd && !config.public.isStaging,
        release: import.meta.env?.SENTRY_RELEASE || process?.env?.SENTRY_RELEASE || '1.0.0',

        // Basic integrations for error tracking
        integrations: [
          sentryReplayIntegration({
            maskAllText: false,
            blockAllMedia: false,
          }),
          // Only add Pinia integration when not in forced mode
          ...(!forceSentryEnabled
            ? [
                {
                  name: 'pinia',
                  setupOnce() {
                    ;(pinia as Pinia).use(({ store }) => {
                      store.$subscribe((_mutation, state: StoreState) => {
                        const sanitizedState = state?.user
                          ? {
                              ...state,
                              user: {
                                ...state.user,
                                email: '[REDACTED]',
                                settings: '[REDACTED]',
                              },
                            }
                          : state

                        Sentry.addBreadcrumb({
                          category: 'pinia',
                          message: `State changed in ${store.$id}`,
                          level: 'info',
                          data: {
                            storeName: store.$id,
                            state: sanitizedState,
                          },
                        })
                      })
                    })
                  },
                },
              ]
            : []),
        ],

        // Disable performance monitoring
        enableTracing: false,
        tracesSampleRate: 0,

        // Keep minimal session replay for error context
        replaysSessionSampleRate: 0,
        replaysOnErrorSampleRate: 1.0,

        // Disable problematic features
        autoSessionTracking: false,

        beforeSend(event) {
          // Don't send events in local testing unless explicitly enabled
          if (!forceSentryEnabled && !config.public.isProd && !config.public.isStaging) {
            return null
          }
          _log('Sentry sending event:', event)
          return event
        },
      })

      _log('Sentry initialized successfully')

      // Set up global error handler
      vueApp.config.errorHandler = (
        err: unknown,
        instance: ComponentPublicInstance | null,
        info: string
      ) => {
        _log('Vue error handler called:', {
          error: err,
          componentName: instance?.$.type.name,
          info,
        })

        Sentry.captureException(err, {
          extra: {
            componentName: instance?.$.type.name,
            info,
          },
        })

        _logError('Global error:', err)
        _logError('Component:', instance)
        _logError('Error Info:', info)
      }
    } catch (initError) {
      _logError('Failed to initialize Sentry:', initError)
    }
  } else {
    _log('Sentry not initialized: Not in production/staging environment and not forced locally')
  }

  return {
    provide: {
      sentry: Sentry,
      sentryEnabled: config.public.isProd || config.public.isStaging || forceSentryEnabled,
    },
  }
})
